'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { ControlDefinition, ControlValues } from '@/types/api';
import { useSite } from '@/lib/site-context';
import { runCode, isTauri } from '@/lib/runtime-bridge';
import {
  UserControlsPanel,
  VisualizationPanel,
  RatingStats,
  RatingForm,
  CommentsSection,
  ShareButton
} from '@/app/components';
import './SimplifiedView.scss';

interface SimplifiedViewProps {
  sketchId: string;
  sketchTitle: string;
  sketchDescription?: string;
  authorUsername?: string;
  authorName?: string;
  code: string;
  controls: ControlDefinition[];
  svgThumbnail?: string | null;
}

export default function SimplifiedView({
  sketchId,
  sketchTitle,
  sketchDescription,
  authorUsername,
  authorName,
  code,
  controls,
  svgThumbnail,
}: SimplifiedViewProps) {
  const site = useSite();
  const searchParams = useSearchParams();
  const [isExecuting, setIsExecuting] = useState(false);
  const [plotData, setPlotData] = useState<any>(null);
  const [plotLayout, setPlotLayout] = useState<any>(null);
  const [gcode, setGcode] = useState<string>('');
  const [runtimeReady, setRuntimeReady] = useState(site.id === 'js');
  const [error, setError] = useState<string | null>(null);
  const [autoUpdate, setAutoUpdate] = useState(true);

  // Initialize control values with defaults, then override with URL params if present
  const initializeControlValues = useCallback(() => {
    const defaultValues: ControlValues = {};
    controls.forEach(control => {
      if (control.type === 'numeric') {
        // Check if URL param exists for this control
        const urlValue = searchParams.get(control.name);
        if (urlValue !== null) {
          const parsedValue = parseFloat(urlValue);
          // Validate the value is within bounds
          if (!isNaN(parsedValue) && parsedValue >= control.min && parsedValue <= control.max) {
            defaultValues[control.name] = parsedValue;
          } else {
            defaultValues[control.name] = control.default;
          }
        } else {
          defaultValues[control.name] = control.default;
        }
      }
    });
    return defaultValues;
  }, [controls, searchParams]);

  const [controlValues, setControlValues] = useState<ControlValues>(() => initializeControlValues());
  const hasAutoRun = useRef(false);
  const executeCodeRef = useRef<((silent?: boolean) => Promise<void>) | null>(null);
  const lastControlValuesRef = useRef<string>(JSON.stringify(controlValues));
  const controlUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const executionTimesRef = useRef<number[]>([]);
  const [avgExecutionTime, setAvgExecutionTime] = useState<number>(0);

  // Rating stats state
  const [ratingStats, setRatingStats] = useState({
    totalRatings: 0,
    avgStars: 0,
    successCount: 0,
    successRate: 0,
  });

  // Load rating stats
  const loadRatingStats = useCallback(async () => {
    try {
      const response = await fetch(`/api/sketches/${sketchId}/ratings`);
      const data = await response.json();

      if (data.success && data.stats) {
        setRatingStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading rating stats:', err);
    }
  }, [sketchId]);

  useEffect(() => {
    loadRatingStats();
  }, [loadRatingStats]);

  useEffect(() => {
    if (site.feature.usePyodide) {
      const initPyodide = async () => {
        try {
          const { initializePyodide } = await import('@/lib/services/pyodideService');
          await initializePyodide();
          setRuntimeReady(true);
        } catch (error) {
          console.error('Failed to initialize Pyodide:', error);
          setError('Failed to initialize Python runtime');
        }
      };
      initPyodide();
    } else {
      setRuntimeReady(true);
    }
  }, [site]);

  const executeCode = useCallback(async (silent: boolean = false) => {
    if (!runtimeReady) return;

    hasAutoRun.current = true;
    const startTime = performance.now();

    if (!silent) {
      setIsExecuting(true);
      setError(null);
      setPlotData(null);
    }

    try {
      // Prepare files for runtime (both JS and Desktop Python)
      const files: Record<string, ArrayBuffer> = {};
      for (const [name, value] of Object.entries(controlValues)) {
        if (value instanceof File) {
          try {
            files[value.name] = await value.arrayBuffer();
          } catch (err) {
            console.error(`Failed to read file ${value.name}:`, err);
          }
        }
      }

      // Handle file uploads for Pyodide (Web Python)
      if (site.id === 'py' && runtimeReady && !isTauri()) {
        try {
          const { getPyodide } = await import('@/lib/services/pyodideService');
          const pyodide = await getPyodide();

          for (const [name, value] of Object.entries(controlValues)) {
            if (value instanceof File) {
              try {
                const arrayBuffer = await value.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);
                pyodide.FS.writeFile(value.name, uint8Array);
              } catch (err) {
                console.error(`Failed to write file ${value.name} to Pyodide FS:`, err);
              }
            }
          }
        } catch (e) {
          console.error('Error preparing Pyodide files:', e);
        }
      }

      // Inject control values into the code
      let modifiedCode = code;

      // For Python, prepend variable assignments
      if (site.id === 'py' || site.id === 'desktop') {
        const assignments = Object.entries(controlValues)
          .map(([name, value]) => {
            if (value instanceof File) return `${name} = "${value.name}"`;
            if (typeof value === 'string') return `${name} = "${value}"`;
            return `${name} = ${value}`;
          })
          .join('\n');
        if (assignments) {
          modifiedCode = `${assignments}\n\n${code}`;
        }
      } else {
        // For JavaScript, prepend variable declarations
        const assignments = Object.entries(controlValues)
          .map(([name, value]) => {
            if (value instanceof File) return `const ${name} = "${value.name}";`;
            if (typeof value === 'string') return `const ${name} = "${value}";`;
            return `const ${name} = ${value};`;
          })
          .join('\n');
        if (assignments) {
          modifiedCode = `${assignments}\n\n${code}`;
        }
      }

      const variant = site.id === 'js' ? 'js' : 'py';
      const data = await runCode(modifiedCode, variant, files, sketchId);

      if (data.success && data.result) {
        const endTime = performance.now();
        const execTime = endTime - startTime;

        // Track execution time
        executionTimesRef.current.push(execTime);
        if (executionTimesRef.current.length > 5) {
          executionTimesRef.current.shift();
        }

        // Calculate average execution time
        const avg = executionTimesRef.current.reduce((a, b) => a + b, 0) / executionTimesRef.current.length;
        setAvgExecutionTime(avg);

        // Promote plot if gcode object previously contained plot (legacy structure)
        if (!data.result.plot && data.result.gcode && typeof data.result.gcode === 'object' && (data.result.gcode as any).plot) {
          data.result.plot = (data.result.gcode as any).plot;
        }

        if (data.result.plot) {
          setPlotData(data.result.plot.data);
          setPlotLayout(data.result.plot.layout);
        }

        // Extract G-code
        if (data.result.gcode) {
          const gVal = data.result.gcode;
          if (typeof gVal === 'string') {
            setGcode(gVal);
          } else if (typeof gVal === 'object' && typeof (gVal as any).gcode === 'string') {
            setGcode((gVal as any).gcode);
          } else {
            setGcode(JSON.stringify(gVal));
          }
        }
      } else {
        setError(data.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Execution error:', error);
      setError('Error executing code');
    } finally {
      if (!silent) {
        setIsExecuting(false);
      }
    }
  }, [code, controlValues, runtimeReady, site]);

  // Keep executeCodeRef up to date
  useEffect(() => {
    executeCodeRef.current = executeCode;
  }, [executeCode]);

  // Auto-run on mount
  useEffect(() => {
    if (!hasAutoRun.current && runtimeReady && code.trim()) {
      executeCode();
    }
  }, [executeCode, runtimeReady, code]);

  // Intelligent auto-update when control values change
  useEffect(() => {
    if (!autoUpdate) return;
    if (!runtimeReady || isExecuting || controls.length === 0) return;
    if (!hasAutoRun.current) return;

    const currentControlValuesStr = JSON.stringify(controlValues);
    if (currentControlValuesStr === lastControlValuesRef.current) {
      return;
    }

    lastControlValuesRef.current = currentControlValuesStr;

    if (controlUpdateTimerRef.current) {
      clearTimeout(controlUpdateTimerRef.current);
    }

    // Calculate intelligent debounce delay
    const DELAY_FACTOR = 2;
    const MIN_DELAY = 30;
    const MAX_DELAY = 1000;

    let debounceDelay = MIN_DELAY;
    if (avgExecutionTime > 0) {
      debounceDelay = Math.max(MIN_DELAY, Math.min(MAX_DELAY, avgExecutionTime * DELAY_FACTOR));
    }

    controlUpdateTimerRef.current = setTimeout(() => {
      if (executeCodeRef.current) {
        executeCodeRef.current(true);
      }
    }, debounceDelay);

    return () => {
      if (controlUpdateTimerRef.current) {
        clearTimeout(controlUpdateTimerRef.current);
      }
    };
  }, [controlValues, runtimeReady, isExecuting, avgExecutionTime, controls.length, autoUpdate]);

  const handleControlsChange = useCallback((newValues: ControlValues) => {
    setControlValues(newValues);
  }, []);

  const handleAutoUpdateChange = useCallback((enabled: boolean) => {
    setAutoUpdate(enabled);
  }, []);

  const handleDownloadGcode = useCallback(() => {
    if (!gcode) return;
    const formattedGcode = formatGcodeWithComments();
    const blob = new Blob([formattedGcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sketchTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.gcode`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [gcode, sketchTitle]);

  const handleCopyGcode = useCallback(() => {
    if (!gcode) return;
    const formattedGcode = formatGcodeWithComments();
    navigator.clipboard.writeText(formattedGcode).then(() => {
      // Could add a toast notification here
      console.log('G-code copied to clipboard');
    });
  }, [gcode]);

  // Place helper functions here, after all hooks and state, before return

  // Helper to get sketch URL (with parameters, like ShareButton)
  const getSketchUrl = () => {
    if (typeof window !== 'undefined') {
      const baseUrl = window.location.origin + window.location.pathname;
      if (controlValues && Object.keys(controlValues).length > 0) {
        const params = new URLSearchParams();
        Object.entries(controlValues).forEach(([key, value]) => {
          params.set(key, String(value));
        });
        const query = params.toString();
        return query ? `${baseUrl}?${query}` : baseUrl;
      }
      return baseUrl;
    }
    return '';
  };

  // Helper to extract parameters from controlValues
  const getParameters = () => {
    if (!controlValues || Object.keys(controlValues).length === 0) return null;
    return Object.entries(controlValues).map(([key, value]) => `${key}: ${JSON.stringify(value)}`);
  };

  // Format G-code with comments
  const formatGcodeWithComments = () => {
    let commentLines: string[] = [];
    const sketchUrl = getSketchUrl();
    if (sketchUrl) commentLines.push(`; Source: "${sketchTitle}" ${sketchUrl}`);
    const params = getParameters();
    if (params) {
      commentLines.push('; Parameters:');
      params.forEach((param: string) => commentLines.push(`;   ${param}`));
    }
    return commentLines.length > 0 ? `${commentLines.join('\n')}\n${gcode}` : gcode;
  };

  return (
    <div className="simplified-view">
      <div className="simplified-header">
        <div className="header-left">
          <div className="brand">
            <a href="/" className="site-name-link">
              <h1 className="site-name">{'<'} {site.displayName}</h1>
            </a>
            {svgThumbnail && (
              <div className="sketch-thumbnail">
                <div
                  className="thumbnail-svg"
                  dangerouslySetInnerHTML={{ __html: svgThumbnail }}
                />
              </div>
            )}
          </div>
          <div className="sketch-meta">
            <h2 className="sketch-title">{sketchTitle}</h2>
            {authorUsername && (
              <p className="author">
                by <a href={`/u/${authorUsername}`}>{authorName || authorUsername}</a>
              </p>
            )}
            {sketchDescription && (
              <p className="description">{sketchDescription}</p>
            )}
          </div>
        </div>
        <div className="header-actions">
          <a href={`/id/${sketchId}`} className="view-code-btn">
            View Code ↗
          </a>
        </div>
      </div>

      <div className="simplified-content">
        <div className="controls-section">
          <div className="controls-header">
            <h2>Controls</h2>
            {controls.length > 0 && (
              <ShareButton controlValues={controlValues} />
            )}
          </div>
          <UserControlsPanel
            controls={controls}
            values={controlValues}
            onChange={handleControlsChange}
            onExecute={executeCode}
            isExecuting={isExecuting}
            autoUpdate={autoUpdate}
            onAutoUpdateChange={handleAutoUpdateChange}
          />

          {/* G-code Section */}
          {gcode && (
            <div className="gcode-section">
              <h2>G-code Output</h2>
              <button
                className="download-gcode-btn"
                onClick={handleDownloadGcode}
              >
                📥 Download G-code
              </button>
              <div className="gcode-content">
                <div className="gcode-header">
                  <button
                    className="btn btn-small"
                    onClick={handleCopyGcode}
                  >
                    📋 Copy
                  </button>
                </div>
                <pre className="gcode-display">{formatGcodeWithComments()}</pre>
              </div>
            </div>
          )}

          {/* Rating Stats */}
          <RatingStats
            totalRatings={ratingStats.totalRatings}
            avgStars={ratingStats.avgStars}
            successCount={ratingStats.successCount}
            successRate={ratingStats.successRate}
          />

          {/* Rating Form */}
          <RatingForm
            sketchId={sketchId}
            currentControlSettings={controlValues}
            onRatingSubmitted={loadRatingStats}
          />

          {/* Comments Section */}
          <CommentsSection sketchId={sketchId} />
        </div>

        <div className="visualization-section">
          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}
          <VisualizationPanel
            plotData={plotData}
            plotLayout={plotLayout}
            svgThumbnail={svgThumbnail}
          />
        </div>
      </div>
    </div>
  );
}
